// Marker glyphs shared by the map and the Layers legend. Types are told apart by
// SHAPE + GLYPH, not only by colour, and every secondary marker stays smaller
// than a live SpotDrop pin.

import L from "leaflet";

const FONT = "'Manrope',system-ui,sans-serif";

const shell = (inner, { size, radius, bg, border, scale = 1, opacity = 1 }) => {
  const s = Math.round(size * scale);
  return `<div style="width:${s}px;height:${s}px;border-radius:${radius};background:${bg};border:1.5px solid ${border};box-shadow:0 1px 4px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;position:relative;opacity:${opacity};pointer-events:auto;-webkit-tap-highlight-color:transparent">${inner}</div>`;
};

const letter = (ch, color, size) =>
  `<span style="font:800 ${size}px ${FONT};color:${color};line-height:1">${ch}</span>`;

const corner = (content, bg, color) =>
  `<span style="position:absolute;right:-4px;bottom:-4px;min-width:11px;height:11px;padding:0 1px;border-radius:999px;background:${bg};color:${color};font:800 8px ${FONT};display:flex;align-items:center;justify-content:center;border:1px solid rgba(10,10,12,0.9);pointer-events:none">${content}</span>`;

const svg = (paths, size) =>
  `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">${paths}</svg>`;

const SCOOTER = svg(
  '<circle cx="5.5" cy="17" r="3"/><circle cx="18.5" cy="17" r="3"/><path d="M8.5 17h6l3-6h-4l-2-3H8.5"/>',
  12
);
const TRUCK = svg(
  '<rect x="2" y="8" width="10" height="7" rx="1"/><path d="M12 10.5h4l4 3.5v1h-8z"/><circle cx="6" cy="17.5" r="1.4"/><circle cx="17" cy="17.5" r="1.4"/>',
  12
);
const BAN = svg('<circle cx="12" cy="12" r="8"/><path d="M6.5 17.5 17.5 6.5"/>', 12);
// Blue Badge bays: the same wheelchair glyph used by live Blue Badge drops.
const WHEELCHAIR = svg(
  '<circle cx="12" cy="4.2" r="1.8"/><path d="M11 7.5v5h5l2.5 5"/><path d="M15.5 14.5a5 5 0 1 1-5.2-6.9"/>',
  13
);

export function placeIconHtml(kind, scale = 1) {
  switch (kind) {
    case "needs_check":
      // A driver report nobody has confirmed yet: deliberately looks unresolved.
      return shell(letter("?", "#fde68a", 12), {
        size: 20, radius: "999px", bg: "rgba(120,53,15,0.78)", border: "rgba(245,158,11,0.9)", scale,
      });
    case "accessible":
    case "accessible_bays":
      return shell(WHEELCHAIR, {
        size: 24, radius: "999px", bg: "#1d4ed8", border: "rgba(255,255,255,0.95)", scale,
      });
    case "free_parking":
      return shell(letter("P", "#052e16", 13) + corner("✓", "#052e16", "#bbf7d0"), {
        size: 24, radius: "7px", bg: "#22c55e", border: "rgba(255,255,255,0.92)", scale,
      });
    case "paid_parking":
      return shell(letter("P", "#ffffff", 13) + corner("€", "#0a0a0c", "#bfdbfe"), {
        size: 24, radius: "999px", bg: "#3b82f6", border: "rgba(255,255,255,0.92)", scale,
      });
    case "car_park":
      return shell(letter("P", "#e4e4e7", 11), {
        size: 20, radius: "6px", bg: "rgba(24,24,27,0.82)", border: "rgba(148,163,184,0.85)", scale,
        opacity: 0.95,
      });
    case "motorcycle_parking":
      return shell(SCOOTER, {
        size: 22, radius: "999px", bg: "#a855f7", border: "rgba(255,255,255,0.9)", scale,
      });
    case "loading_bay":
      return shell(TRUCK, {
        size: 22, radius: "6px", bg: "#f59e0b", border: "rgba(255,255,255,0.9)", scale,
      });
    case "residents_only":
      return shell(letter("R", "#fecaca", 11), {
        size: 19, radius: "6px", bg: "rgba(127,29,29,0.72)", border: "rgba(239,68,68,0.9)", scale,
        opacity: 0.92,
      });
    case "no_parking":
      return shell(BAN, {
        size: 19, radius: "999px", bg: "rgba(127,29,29,0.72)", border: "rgba(239,68,68,0.9)", scale,
        opacity: 0.92,
      });
    default:
      return shell(letter("i", "#e4e4e7", 11), {
        size: 19, radius: "6px", bg: "rgba(24,24,27,0.82)", border: "rgba(148,163,184,0.8)", scale,
      });
  }
}

const SIZES = {
  needs_check: 20,
  accessible: 24,
  accessible_bays: 24,
  free_parking: 24,
  paid_parking: 24,
  car_park: 20,
  motorcycle_parking: 22,
  loading_bay: 22,
  residents_only: 19,
  no_parking: 19,
};

const cache = new Map();

export function placeIcon(kind, scale = 1) {
  const key = `${kind}|${scale}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const s = Math.round((SIZES[kind] || 19) * scale);
  const icon = L.divIcon({
    html: placeIconHtml(kind, scale),
    className: "spotdrop-place-icon",
    iconSize: [s, s],
    iconAnchor: [s / 2, s / 2],
  });
  cache.set(key, icon);
  return icon;
}

export function clusterIcon(count, scale = 1) {
  const s = Math.round(24 * scale);
  return L.divIcon({
    html: `<div style="width:${s}px;height:${s}px;border-radius:999px;background:rgba(16,16,20,0.62);border:1px solid rgba(255,255,255,0.18);color:#d4d4d8;font:700 11px ${FONT};display:flex;align-items:center;justify-content:center;backdrop-filter:blur(4px)">${count}</div>`,
    className: "",
    iconSize: [s, s],
  });
}
